/**
 * Admissions Committee review — API client.
 *
 * Follows `lib/policies/api.ts`: one BASE, one private `req()`, one thin named
 * export per endpoint. No Authorization header — the HttpOnly cookie does it,
 * and `fetchWithAuth` handles the silent refresh.
 */
import { fetchWithAuth, API_BASE } from "@/lib/auth";

const BASE = `${API_BASE}/api/v1/admissions-committee`;

async function req(path: string, options: RequestInit = {}) {
  const res = await fetchWithAuth(`${BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    // The status rides along: a 403 (not a committee member) and a 401 (the
    // session changed under this tab) each deserve their own explanation, and
    // without this they are indistinguishable from a genuine failure.
    throw Object.assign(new Error(data.detail || `Request failed (${res.status})`), {
      status: res.status,
    });
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

export type ReviewStatus = "received" | "in_review" | "closed" | "cancelled";
export type VoteValue = "approve" | "deny" | "defer";
export type ChecklistDecision = "approve" | "defer" | "flag_board";

/**
 * Where a closed review went in ChartRoom. Every committee close — approve,
 * deny, defer, or a Section F flag whatever the votes said — goes to the Board
 * of Directors, who decide in the Hub.
 */
export type RoutedTo = "board_review";

/** The plugin's closed rejection taxonomy — optional on a Board denial. */
export const BOARD_REJECTION_REASONS = [
  ["work_experience", "Work experience"],
  ["competence", "Competence"],
  ["ethics", "Ethics"],
  ["documentation", "Documentation"],
  ["other", "Other"],
] as const;
export type BoardRejectionReason = (typeof BOARD_REJECTION_REASONS)[number][0];

export interface BoardDecisionInfo {
  outcome: VoteValue | null;
  rationale: string | null;
  rejection_reason?: BoardRejectionReason | null;
  batch_ref: string;
  decided_at: string | null;
  report: { status: string | null; detail: string | null };
}

export interface Progress {
  roster_size: number;
  votes_cast: number;
  outstanding: number;
}

export interface ReviewSummary {
  id: string;
  application_id: number;
  applicant_name: string;
  status: ReviewStatus;
  received_at: string | null;
  checklist_status: "draft" | "final" | null;
  viewer_has_voted: boolean;
  /** The viewer's own direction, for the "You voted · Approve" chip. */
  viewer_vote: VoteValue | null;
  progress: Progress;
  outcome: VoteValue | null;
  /** Null until closed. Optional so an older backend simply shows no route. */
  routed_to?: RoutedTo | null;
  board_outcome?: VoteValue | null;
  /** An open request to the applicant — the queue's "Awaiting applicant" group. */
  awaiting_applicant?: boolean;
}

export type InfoRequestStatus = "open" | "answered" | "no_response";

/** Something the committee asked the applicant for, and what came back. */
export interface InfoRequest {
  id: string;
  row: "consider_additional_docs" | "consider_clarification";
  label: string;
  ask: string;
  status: InfoRequestStatus;
  requested_by: string | null;
  requested_at: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  /** The applicant's written reply, or staff's note on a no-response close. */
  response_note: string | null;
  /** Files staff uploaded from the reply, as named in the Documents tab. */
  documents: string[];
}

export interface ReviewQueue {
  awaiting: ReviewSummary[];
  /** Open, not voted on by this viewer, and waiting on the applicant. */
  awaiting_applicant: ReviewSummary[];
  completed: ReviewSummary[];
}

export interface ChecklistPayload {
  id?: string;
  status: "draft" | "final" | null;
  fields: Record<string, unknown>;
  docs: Record<string, unknown>;
  decision: ChecklistDecision | null;
  completed_at?: string | null;
  /** Who last touched the shared checklist, derived from the audit trail. The
   *  checklist is one object for the whole committee, so this is what stops a
   *  silent clobber between two reviewers. */
  last_edited_by?: string | null;
  last_edited_at?: string | null;
  /** Section E confirmations, by row. A Yes reaches staff only once confirmed. */
  followups?: Record<string, FollowUpState>;
}

export interface FollowUpState {
  confirmed_at: string | null;
  confirmed_by: string | null;
  /** The ask as confirmed. A local edit that departs from it is unconfirmed. */
  note: string | null;
  /** When this confirmation went to staff in the daily follow-up, if it has. */
  reported_at: string | null;
}

export interface ReviewDetail {
  id: string;
  application_id: number;
  applicant_name: string;
  status: ReviewStatus;
  received_at: string | null;
  /**
   * The plugin's curated packet, passed through untouched.
   *
   * Deliberately untyped: the shape is owned by the WordPress plugin (see
   * CONTRACT.md §①) and the Hub is explicitly forbidden from re-deriving or
   * re-shaping it. Declaring a type here would be this app asserting a contract
   * it does not own, and would silently drop any field the plugin adds.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  packet: Record<string, any>;
  checklist: ChecklistPayload;
  progress: Progress;
  /** Voting is open (C-1): `vote` carries the direction for a viewer entitled
   *  to it — the committee and its staff liaison — and is null for a staff
   *  observer, who gets the aggregate `tally` instead. */
  roster: Array<{
    user_id: string | null;
    display_name: string;
    is_chair: boolean;
    has_voted: boolean;
    vote: VoteValue | null;
    voted_at: string | null;
    /** The chase trail: when this voter was last nudged, by whom, and when they
     *  may be nudged again. Null when never reminded. */
    last_reminded_at: string | null;
    next_eligible_at: string | null;
    reminder_count: number;
    last_reminded_by: string | null;
  }>;
  viewer: {
    is_chair: boolean;
    /** Holds a liaison assignment on the Admissions Committee — the same
     *  early-close authority as the chair (C-2). */
    is_liaison: boolean;
    sees_vote_directions: boolean;
    /** Committee members only — the same rule the save route enforces. Read it
     *  before writing anything the reviewer did not type (the Section D
     *  autofill), so a read-only staff viewer opening a review cannot provoke a
     *  403 autosave. */
    can_edit_checklist: boolean;
    is_voter: boolean;
    has_voted: boolean;
    vote: VoteValue | null;
    vote_comments: string | null;
    voted_at: string | null;
  };
  can_vote: boolean;
  close: { allowed: boolean; reason: string | null };
  /** The majority rule, live: `outcome` is non-null once ⌊n/2⌋+1 of the frozen
   *  roster have voted the same way, at which point the rest cannot change it. */
  majority: { threshold: number; outcome: VoteValue | null };
  /** The running tally, open or closed. It used to appear only at the close;
   *  that was the blind-voting boundary, removed deliberately (C-1). */
  tally?: { approve: number; deny: number; defer: number; total: number };
  outcome?: VoteValue | null;
  outcome_rationale?: string | null;
  closed_at?: string | null;
  closed_early?: boolean;
  /** The committee's own report to ChartRoom — sent for every close. */
  report?: { status: string | null; detail: string | null };
  routed_to?: RoutedTo | null;
  board_decision?: BoardDecisionInfo | null;
  /** What the committee asked the applicant for, and what came back. */
  info_requests?: InfoRequest[];
}

export interface DocumentLink {
  name: string;
  kind: "work_sample" | "supporting";
  /**
   * Whether the browser can render this file in the page. Decided server-side
   * from the extension (`documents.is_inline`) rather than re-derived here, so
   * the committee's screen, the board's screen and the proxy's own
   * `Content-Disposition` cannot disagree about the same file.
   */
  inline: boolean;
  /** Known only once the document is cached in the Hub's storage. */
  size_bytes: number | null;
  /**
   * Office files are shown as a server-converted PDF, never as their original
   * bytes — a browser cannot render a .pptx, so serving one would be a silent
   * download rather than a preview. This says whether that conversion has
   * happened, so the list can label a row without opening it.
   *
   * `not_applicable` needs no conversion (PDF, image, text) · `pending` is being
   * prepared · `ready` will open · `password_protected` and `failed` never will.
   */
  preview_state?: PreviewState;
  /** `staff` = sent by the applicant in answer to a committee request, uploaded by staff. */
  source?: "chartroom" | "staff";
  added_at?: string | null;
}

export type PreviewState =
  | "not_applicable"
  | "pending"
  | "ready"
  | "password_protected"
  | "failed";

export const listReviews = (): Promise<ReviewQueue> =>
  req("/reviews").then((q: ReviewQueue) => ({ ...q, awaiting_applicant: q.awaiting_applicant ?? [] }));

/** One applicant on staff's "Awaiting applicant" worklist. */
export interface StaffInfoRequestGroup {
  review_id: string;
  application_id: number;
  applicant_name: string;
  contact: { email: string | null; company: string | null; location: string | null };
  requests: InfoRequest[];
  /** The email to send the applicant, from the open asks. Null once all are closed. */
  draft: { subject: string; body: string } | null;
  waiting_since?: string;
}

export const listStaffInfoRequests = (): Promise<{
  waiting: StaffInfoRequestGroup[];
  resolved: StaffInfoRequestGroup[];
}> => req("/staff/info-requests");

/** Send the applicant's reply to the committee. Multipart, so not through `req()`,
 *  which sets a JSON content type; the browser must set the multipart boundary. */
export async function resolveInfoRequest(
  requestId: string,
  outcome: "answered" | "no_response",
  note: string,
  files: File[],
): Promise<{ documents: string[]; notified: number }> {
  const body = new FormData();
  body.append("outcome", outcome);
  if (note.trim()) body.append("note", note);
  files.forEach((f) => body.append("files", f));
  const res = await fetchWithAuth(`${BASE}/staff/info-requests/${requestId}/resolve`, {
    method: "POST",
    body,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw Object.assign(new Error(data.detail || `Request failed (${res.status})`), { status: res.status });
  }
  return res.json();
}

export const getReview = (id: string): Promise<ReviewDetail> => req(`/reviews/${id}`);

export const saveChecklist = (
  id: string,
  fields: Record<string, unknown>,
  docs?: Record<string, unknown>,
  acceptedSuggestions?: string[],
): Promise<ChecklistPayload> =>
  req(`/reviews/${id}/checklist`, {
    method: "POST",
    body: JSON.stringify({
      fields,
      docs,
      accepted_suggestions: acceptedSuggestions ?? [],
    }),
  });

/** Send one Section E ask to staff. The note is sent with it, so the text
 *  confirmed is the text staff receive. */
export const confirmFollowUp = (id: string, row: string, note: string): Promise<ChecklistPayload> =>
  req(`/reviews/${id}/checklist/followups/${row}/confirm`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });

/** One suggested answer for a checklist row, with the evidence behind it. */
export interface Suggestion {
  /** `null` means "here is the evidence, you decide" — see the employer cap. */
  value: boolean | null;
  basis: "packet" | "cmt_records" | "linkedin" | "none";
  evidence: string[];
  confident: boolean;
}

export interface ChecklistSuggestions {
  applicant: {
    email?: string | null;
    company?: string | null;
    matched: boolean;
    member_id?: number | null;
    experience_track?: string | null;
  };
  fields: Record<string, Suggestion>;
  notes: string[];
  /** Where each reference might work — hints for the employer cap, never an answer. */
  references?: ReferenceEmployerHint[];
  /** Who works where according to LinkedIn; null when the review was never checked. */
  employer_check?: EmployerCheck | null;
}

/** A company as LinkedIn names it, with its LinkedIn page. */
export interface LinkedInCompany {
  id?: string;
  name?: string;
  url?: string;
}

/**
 * - `same` / `different` — LinkedIn places them at / away from the applicant's employer.
 * - `possibly_same` — a different LinkedIn company, but the applicant's work email domain (a sister company?).
 * - `unknown` — no profile found, or no current employer listed.
 * - `not_compared` — the applicant's own employer could not be found.
 * - `not_checked` — this reference was not part of the check.
 */
export type SponsorEmployerStatus =
  | "same"
  | "different"
  | "possibly_same"
  | "likely_same"
  | "unknown"
  | "not_compared"
  | "not_checked";

export interface EmployerCheck {
  /** yes/no are decisive and autofill the employer-cap row; open/unresolved never do. */
  outcome: "yes" | "no" | "open" | "unresolved";
  /** One plain-English sentence explaining the outcome. */
  reason: string;
  checked_at: string | null;
  applicant: { name: string | null; linkedin_url: string | null; companies: LinkedInCompany[] };
  sponsors: Array<{
    name: string | null;
    email: string;
    linkedin_url: string | null;
    url_source: string | null;
    companies: LinkedInCompany[];
    status: SponsorEmployerStatus;
    shared_domain: string | null;
  }>;
}

/**
 * What the platform holds about a reference's employer, which is little: no
 * source records it. A shared *work* email domain with the applicant (free
 * mail providers never count) and the company on their own Hub profile.
 */
export interface ReferenceEmployerHint {
  name: string | null;
  email: string;
  email_domain: string | null;
  shares_applicant_domain: boolean;
  hub_profile_company: string | null;
}

/**
 * Derived evidence for the bylaws §2.01(C) rows.
 *
 * A read — this endpoint never writes. The rail fills the confident ones into
 * the checklist on open, but it does so through the ordinary `saveChecklist`
 * path, exactly as a member clicking the row would.
 */
export const getChecklistSuggestions = (id: string): Promise<ChecklistSuggestions> =>
  req(`/reviews/${id}/checklist/suggestions`);

export const finaliseChecklist = (
  id: string,
  fields: Record<string, unknown>,
  decision: ChecklistDecision,
  decisionRationale: string,
  docs?: Record<string, unknown>,
): Promise<ChecklistPayload> =>
  req(`/reviews/${id}/checklist/finalise`, {
    method: "POST",
    body: JSON.stringify({ fields, docs, decision, decision_rationale: decisionRationale }),
  });

export interface RemindResult {
  sent: string[];
  sent_ids: string[];
  /** Recipients skipped because they were reminded recently — carrying *when*
   *  they become eligible again, so the chair is told rather than left
   *  wondering whether the click worked. */
  skipped: Array<{
    name: string;
    user_id: string;
    last_reminded_at: string;
    next_eligible_at: string;
  }>;
  /** Whether the rows will actually be dispatched.
   *
   *  `"held"` means admissions email is switched off server-side: the reminders
   *  are durably queued and will send when it is switched on, but nothing has
   *  left the building. The UI must say so — reporting "Reminded 1 voter." for
   *  a message that will never arrive is the bug this field exists to close. */
  delivery: "queued" | "held";
  mailer_enabled: boolean;
  /** Test mode: delivered to the redirect address rather than to the roster. */
  redirected: boolean;
}

/** Nudge outstanding voters. Omit `userIds` to remind everyone still to vote. */
export const remindVoters = (id: string, userIds?: string[]): Promise<RemindResult> =>
  req(`/reviews/${id}/remind`, {
    method: "POST",
    body: JSON.stringify(userIds ? { user_ids: userIds } : {}),
  });

export const castVote = (id: string, vote: VoteValue, comments: string): Promise<ReviewDetail> =>
  req(`/reviews/${id}/vote`, { method: "POST", body: JSON.stringify({ vote, comments }) });

export const closeReview = (id: string, rationale: string): Promise<ReviewDetail> =>
  req(`/reviews/${id}/close`, { method: "POST", body: JSON.stringify({ rationale }) });

export const retryReport = (id: string): Promise<{ status: string | null; detail: string | null }> =>
  req(`/reviews/${id}/report/retry`, { method: "POST" });

/** Links are minted on demand and expire in an hour — never cache them. */
export const getDocuments = (id: string): Promise<{ documents: DocumentLink[] }> =>
  req(`/reviews/${id}/documents`);

/**
 * Fetch one document's bytes through the Hub's own proxy.
 *
 * Never `downloadUrl` directly: that points at ChartRoom, so a frame pointed at
 * it is at the mercy of headers we do not control and fails silently when it is
 * refused. The proxy re-mints the link server-side on every call, which is also
 * why nothing here needs to think about `expiresAt` — an hour-old tab works.
 *
 * Returns a Blob so the caller owns the object URL's lifetime and can revoke it.
 * A plain `<a href>` would skip `fetchWithAuth`'s silent refresh and 401 on an
 * expired access token.
 *
 * Note the response is deliberately never `downloadUrl` — the list endpoint no
 * longer returns one. A session-free signed link in a JSON response the page can
 * read would hand out exactly what this proxy exists to keep inside.
 */
async function fetchDocument(path: string, name: string): Promise<Blob> {
  const res = await fetchWithAuth(`${BASE}${path}?name=${encodeURIComponent(name)}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw Object.assign(new Error(data.detail || `Request failed (${res.status})`), {
      status: res.status,
    });
  }
  return res.blob();
}

export const fetchDocumentBlob = (reviewId: string, name: string): Promise<Blob> =>
  fetchDocument(`/reviews/${reviewId}/documents/content`, name);

export const fetchBoardDocumentBlob = (reviewId: string, name: string): Promise<Blob> =>
  fetchDocument(`/board/reviews/${reviewId}/documents/content`, name);

export const fetchDocumentNotes = (
  reviewId: string,
  name: string,
): Promise<{ name: string; notes: Record<string, string> }> =>
  req(`/reviews/${reviewId}/documents/notes?name=${encodeURIComponent(name)}`);

export const fetchBoardDocumentNotes = (
  reviewId: string,
  name: string,
): Promise<{ name: string; notes: Record<string, string> }> =>
  req(`/board/reviews/${reviewId}/documents/notes?name=${encodeURIComponent(name)}`);

// ── access ───────────────────────────────────────────────────────────────────

export interface AdmissionsAccess {
  is_committee_member: boolean;
  is_chair: boolean;
  is_board_member: boolean;
  is_staff: boolean;
  /** Resolved server-side, so a group page can tell whether it IS this committee. */
  admissions_committee_id: number | null;
  board_committee_id: number | null;
  /**
   * Applications still waiting on this viewer's vote — the same rule as the
   * "Awaiting" bucket of `listReviews`. Always 0 for staff, who may read the
   * queue but never vote in it.
   */
  awaiting_count: number;
  /**
   * Board-queue applications the Board has not decided — the same rule as the
   * "awaiting" bucket of the board queue. 0 for anyone who is not a board
   * member. Optional so an older backend simply badges nothing.
   */
  board_awaiting_count?: number;
}

/**
 * What this viewer may do. A capability call rather than a client-side check on
 * the user's assignments: `entity_name` is optional on the assignment shape and
 * is often absent, so matching the committee by name in the browser would hide
 * the nav entry from real members.
 */
export const getAccess = (): Promise<AdmissionsAccess> => req("/access");

/**
 * The one place that knows where the review queue lives.
 *
 * It is a tab on the Admissions Committee's own group dashboard — there is no
 * separate member-sidebar section any more. Every surface that points at the
 * queue (Home card, nav badges, bell, email redirect) comes through here, so
 * moving the queue is a one-line change rather than a scavenger hunt.
 *
 * `reviewId` preselects a specific application on arrival.
 */
export function admissionsQueueUrl(
  committeeId: number | null | undefined,
  reviewId?: string | null,
): string {
  // Without a resolved committee id there is no group page to land on; Home is
  // the honest fallback rather than a URL that 404s.
  if (committeeId === null || committeeId === undefined) return "/dashboard";
  const suffix = reviewId ? `&review=${encodeURIComponent(reviewId)}` : "";
  return `/dashboard/groups/committee/${committeeId}?tab=admissions${suffix}`;
}

// ── board ratification ───────────────────────────────────────────────────────

export interface BoardQueueRow {
  id: string;
  application_id: number;
  applicant_name: string;
  received_at: string | null;
  closed_at: string | null;
  committee_outcome: VoteValue | null;
  committee_rationale: string | null;
  /** Visible here because every review in this queue is already closed. */
  tally: { approve: number; deny: number; defer: number; total: number };
  flag_rationale: string | null;
  /** Why it is in the queue: Section F flagged it, or it is the committee's
   *  majority (approve, deny or defer) coming up for the Board's final say. */
  route_reason?: "flagged" | "committee_decision";
  /** The committee's close must reach ChartRoom (putting the application in
   *  Board Review there) before the Board's decision can. */
  committee_report?: { status: string | null; detail: string | null };
  experience_track: string | null;
  /** "Toronto, Canada" — lifted from the packet so the table can show it. */
  location: string | null;
  experience_years: number | null;
  /** No numeric rating exists in the packet; referees recommend, not score. */
  references: { total: number; completed: number; recommend_approve: number };
  board_decision: BoardDecisionInfo | null;
}

/**
 * One Board-queue application opened for reading. The queue row plus the packet —
 * and nothing else: the board does not vote, so no checklist, roster or viewer
 * block travels here. `packet` is left untyped for the reason `ReviewDetail`
 * gives: the plugin owns that shape.
 */
export interface BoardReviewDetail extends BoardQueueRow {
  status: ReviewStatus;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  packet: Record<string, any>;
  /** The committee's Step 1 evaluation, read-only — Section F is why this
   *  review is in front of the board at all. */
  checklist: ChecklistPayload;
  read_only: true;
}

export interface RatifyResult {
  ok: boolean;
  ratified: number;
  batch_ref: string;
  outcome: VoteValue;
  results: Array<{
    review_id: string;
    applicant_name: string;
    report_status: string | null;
    report_detail: string | null;
  }>;
}

export const getBoardQueue = (): Promise<{ awaiting: BoardQueueRow[]; ratified: BoardQueueRow[] }> =>
  req("/board/queue");

export const getBoardReview = (id: string): Promise<BoardReviewDetail> =>
  req(`/board/reviews/${id}`);

/** Board's own document links — the committee's endpoint is closed to them. */
export const getBoardDocuments = (id: string): Promise<{ documents: DocumentLink[] }> =>
  req(`/board/reviews/${id}/documents`);

/**
 * The canonical link to the Board's ratification queue — the twin of
 * `admissionsQueueUrl`, and for the same reason: the queue is a tab on the
 * Board of Directors' group dashboard, not a sidebar section, so every surface
 * pointing at it comes through here.
 */
export function boardQueueUrl(
  committeeId: number | null | undefined,
  reviewId?: string | null,
): string {
  if (committeeId === null || committeeId === undefined) return "/dashboard";
  const suffix = reviewId ? `&review=${encodeURIComponent(reviewId)}` : "";
  return `/dashboard/groups/committee/${committeeId}?tab=board${suffix}`;
}

/** One batch: many reviews sharing one outcome, rationale and batch label. */
export const ratifyBatch = (
  reviewIds: string[],
  outcome: VoteValue,
  rationale: string,
  batchRef: string,
  rejectionReason?: BoardRejectionReason | null,
): Promise<RatifyResult> =>
  req("/board/ratify", {
    method: "POST",
    body: JSON.stringify({
      review_ids: reviewIds,
      outcome,
      rationale,
      batch_ref: batchRef,
      // Only a denial carries a category; the server refuses it on anything else.
      rejection_reason: outcome === "deny" ? rejectionReason || null : null,
    }),
  });

export const retryBoardReport = (
  id: string,
): Promise<{ status: string | null; detail: string | null }> =>
  req(`/board/${id}/report/retry`, { method: "POST" });
